import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { App } from '@/types/app';

interface RedeployDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: App;
  onSuccess: () => void;
}

interface EnvVarEntry {
  key: string;
  value: string;
}

function entriesFrom(envVars: App['env_vars']): EnvVarEntry[] {
  return envVars ? Object.entries(envVars).map(([key, value]) => ({ key, value })) : [];
}

export function RedeployDialog({ open, onOpenChange, app, onSuccess }: RedeployDialogProps) {
  // Initialize from app's current env_vars when opened, or when they change while open
  const [envVars, setEnvVars] = useState<EnvVarEntry[]>(() => (open ? entriesFrom(app.env_vars) : []));
  const [synced, setSynced] = useState({ open, envSource: app.env_vars });
  if (synced.open !== open || synced.envSource !== app.env_vars) {
    setSynced({ open, envSource: app.env_vars });
    if (open) setEnvVars(entriesFrom(app.env_vars));
  }
  const [loading, setLoading] = useState(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();

  const addEntry = () => {
    setEnvVars([...envVars, { key: '', value: '' }]);
  };

  const removeEntry = (index: number) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...envVars];
    updated[index][field] = value;
    setEnvVars(updated);
  };

  const handleSave = async () => {
    // Validate: no empty keys
    const hasEmptyKey = envVars.some(e => e.key.trim() === '');
    if (hasEmptyKey) {
      toast.error('Environment variable keys cannot be empty');
      return;
    }

    // Convert to map
    const envVarsMap: Record<string, string> = {};
    for (const entry of envVars) {
      if (entry.key.trim()) {
        envVarsMap[entry.key.trim()] = entry.value;
      }
    }

    setLoading(true);
    try {
      await api.apps.redeploy(app.id, envVarsMap);
      toast.success('Redeploy initiated');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to redeploy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onOpenAutoFocus={() => captureOpener()}
        onCloseAutoFocus={restoreFocus}
      >
        <DialogHeader>
          <DialogTitle>Redeploy App</DialogTitle>
          <DialogDescription>
            Review or modify environment variables for '{app.name}' before initiating redeployment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {envVars.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor={`key-${index}`} className="sr-only">Key</Label>
                  <Input
                    id={`key-${index}`}
                    placeholder="KEY"
                    value={entry.key}
                    onChange={(e) => updateEntry(index, 'key', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`value-${index}`} className="sr-only">Value</Label>
                  <Input
                    id={`value-${index}`}
                    placeholder="value"
                    value={entry.value}
                    onChange={(e) => updateEntry(index, 'value', e.target.value)}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeEntry(index)}
                className="shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addEntry}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Variable
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Redeploy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}